"use client";

import { ReactElement } from "react";
import { QuizCTA } from "~/components/features/quiz";
import Container from "~/componentsDS/Layout/Container";
import EnhancedTestimonials from "~/componentsDS/EnhancedTestimonials";
import Badge from "~/componentsDS/Badge";
import TextWithRegisteredMark from "~/componentsDS/TextWithRegisteredMark";
import Heading from "~/componentsDS/Typography/Heading";
import Reveal from "~/componentsDS/Reveal";
import {
    TestimonialV3,
    VideoTrackingConfig,
} from "~/componentsDS/EnhancedTestimonials/data";
import { cn } from "~/lib/utils";
import ReviewsRating from "~/components/Rating/ReviewsRating";
import { GTM_EVENT_NAMES } from "~/constants/gtmEvents";
import { QUIZ_ROUTES, QuizRoute } from "~/constants/quizRoutes";
import type { GtmEvent } from "~/lib/gtm/getGtmEventData";

type Props = {
    reviewsData?: TestimonialV3[];
    headerElem?: ReactElement;
    isFullScreenCarousel?: boolean;
    sectionClassName?: string;
    ctaText?: string;
    ctaLink?: QuizRoute;
    ctaEventData?: GtmEvent["value"];
    videoTrackingConfig?: VideoTrackingConfig;
};

const ReviewsTestimonialsSection = ({
    reviewsData,
    headerElem,
    isFullScreenCarousel = true,
    sectionClassName,
    ctaText,
    ctaLink = QUIZ_ROUTES.HORMONE_IMBALANCE,
    ctaEventData,
    videoTrackingConfig,
}: Props) => {
    const resolvedCtaEventData = ctaEventData ?? {
        event_name: GTM_EVENT_NAMES.LP_INTERACTION,
        widget: "reviews_testimonials",
        meta_data: {
            click_text: ctaText,
        },
    };

    return (
        <section className={cn("pb-[52px] md:pb-[96px]", sectionClassName)}>
            <Container>
                <div className="flex flex-col md:flex-row md:justify-between md:items-end">
                    <div>
                        <Reveal>
                            <Badge className="mb-2 md:mb-4 font-medium">
                                Real people, real testimonials
                            </Badge>
                            {headerElem ? (
                                headerElem
                            ) : (
                                <Heading
                                    as="h2"
                                    size="sm"
                                    sizes={{ md: "md" }}
                                    className="text-primary500 mb-4 md:mb-3"
                                >
                                    Hear from{" "}
                                    <Heading
                                        as="i"
                                        font="featureDisplay"
                                        weight="400"
                                        className="text-[28px] md:text-[36px]"
                                    >
                                        5000+{" "}
                                    </Heading>
                                    women
                                </Heading>
                            )}
                        </Reveal>
                    </div>
                    <ReviewsRating />
                </div>
            </Container>

            <div className="relative mt-8">
                <EnhancedTestimonials
                    isFullScreenCarousel={isFullScreenCarousel}
                    testimonials={reviewsData}
                    trackingConfig={videoTrackingConfig}
                />
            </div>

            {ctaText && (
                <Container className="mt-8 flex justify-center">
                    <QuizCTA
                        variant="primary"
                        size="md"
                        href={ctaLink}
                        eventData={resolvedCtaEventData}
                        className="w-full md:w-auto"
                    >
                        <TextWithRegisteredMark text={ctaText} />
                    </QuizCTA>
                </Container>
            )}
        </section>
    );
};

export default ReviewsTestimonialsSection;
